import {
    ChangeDetectorRef,
    Directive,
    EmbeddedViewRef,
    Injector,
    Input,
    NgIterable,
    TemplateRef,
    ViewContainerRef
} from '@angular/core';
import {SCROLLBAR} from '../../custom-scrollbar/scrollbar';
import {destroy$} from '../../destroyable';
import {ElementRulerDirective} from '../../element-ruler';
import {TableAvailableRowsObserver} from './table-available-rows-observer';
import {TableEmptyRows} from './table-empty-rows';

export interface TableBodyRowContext<T> {
    $implicit: T;
    index: number;
}

@Directive({
    selector: '[tableBodyRow]',
    standalone: false
})
export class TableBodyRowDirective<T = any> {

    public readonly destroy$ = destroy$();

    /**
     * Argument used for type assertion in template only.
     * Usage: <tr *tableBodyRow="let row of table.rows">
     */
    @Input()
    public tableBodyRowOf: NgIterable<T>;

    public get tableAvailableRowsObserver(): TableAvailableRowsObserver {
        return this._tableAvailableRowsObserver = this._tableAvailableRowsObserver
            || new TableAvailableRowsObserver(
                this.viewContainerRef,
                this.templateRef,
                this.containerRulerDirective,
                this.changeDetectorRef
            );
    }

    public get tableEmptyRows(): TableEmptyRows {
        return this._tableEmptyRows = this._tableEmptyRows
            || new TableEmptyRows(
                this.viewContainerRef,
                this.templateRef,
                this.tableAvailableRowsObserver.availableRowCount$,
                this.changeDetectorRef,
                this.destroy$,
                this.injector.get(SCROLLBAR, null)
            );
    }

    public get containerRulerDirective(): ElementRulerDirective {
        return this._containerRulerDirective = this._containerRulerDirective
            || this.injector.get(ElementRulerDirective);
    }

    protected _tableAvailableRowsObserver: TableAvailableRowsObserver;
    protected _tableEmptyRows: TableEmptyRows;
    protected _containerRulerDirective: ElementRulerDirective;

    constructor(
        public viewContainerRef: ViewContainerRef,

        /**
         * Type defined here for {@link templateRef} is used by IDE for type checking in template.
         * Here changing to type "<T | any>" could do two things:
         * 1) allow to omit "of" argument: <tr *tableBodyRow="let row">;
         * 2) highlight unknown fields as hint, but not as error.
         */
        public templateRef: TemplateRef<TableBodyRowContext<T>>,

        protected changeDetectorRef: ChangeDetectorRef,
        protected injector: Injector
    ) {}

    public createRow(item: T, index: number): EmbeddedViewRef<TableBodyRowContext<T>> {
        const context: TableBodyRowContext<T> = {
            $implicit: item,
            index
        };

        return this.viewContainerRef.createEmbeddedView(
            this.templateRef,
            context,
            index
        );
    }

}
